// Platform console -> AI statements & settings. G4 of
// docs/HMS_V4_AI_PROVIDER_MANAGEMENT.md §7.4's manual-invoicing tracker (D30)
// plus the platform-wide FX rate / reconciliation threshold / tax-review
// gate (D29) — combined into one screen since both are platform-wide
// (not per-tenant) AI billing administration.
import { useEffect, useState } from 'react';
import { platformApi, platformAuthHeader } from '../lib/platformApi/client';
import type { PlatformTenant } from './types';
import { ConfirmDialog } from './ConfirmDialog';

// A plain <a href="/platform-api/..."> can't carry the operator's Bearer
// token (platform auth has no cookie/session fallback — see
// middleware/platformAuth.js), so the browser's own navigation would hit it
// unauthenticated. Fetch with the header instead and save the blob, same
// pattern frontend/src/api.ts already uses for authenticated document
// downloads.
async function downloadWithAuth(url: string, filenameFallback: string) {
  const res = await fetch(url, { headers: platformAuthHeader() });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') || '';
  const nameMatch = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = nameMatch?.[1] ? decodeURIComponent(nameMatch[1]) : filenameFallback;
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
}

interface Statement {
  id: number;
  tenant_id: number;
  tenant_name: string;
  tenant_slug: string;
  period_month: string;
  allowance_inr: number | null;
  total_charge_inr: number | null;
  overage_inr: number | null;
  status: 'draft' | 'finalised' | 'invoiced';
  finalised_by: string | null;
  finalised_at: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  invoiced_by: string | null;
}

interface PlatformSettings {
  usd_inr_rate: number | null;
  reconciliation_variance_alert_pct: number;
  tax_review_completed_at: string | null;
  tax_review_note: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

const STATUS_STYLE: Record<Statement['status'], { bg: string; color: string }> = {
  draft: { bg: '#F3F4F6', color: '#6B7280' },
  finalised: { bg: '#FEF3C7', color: '#92400E' },
  invoiced: { bg: '#DCFCE7', color: '#166534' },
};

const thisMonthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

interface AiStatementsScreenProps {
  onBack: () => void;
}

export default function AiStatementsScreen({ onBack }: AiStatementsScreenProps) {
  const [statements, setStatements] = useState<Statement[]>([]);
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [rateInput, setRateInput] = useState('');
  const [genTenantId, setGenTenantId] = useState<number | ''>('');
  const [genPeriod, setGenPeriod] = useState(thisMonthStart());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [confirmTaxReview, setConfirmTaxReview] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState<Record<number, { number: string; date: string }>>({});

  const refresh = () => {
    platformApi.get<{ statements: Statement[] }>('/ai/statements').then(d => setStatements(d.statements)).catch(e => setError(e instanceof Error ? e.message : String(e)));
    platformApi.get<PlatformTenant[]>('/tenants').then(setTenants).catch(() => {});
    platformApi.get<{ settings: PlatformSettings }>('/ai/platform-settings').then(d => { setSettings(d.settings); setRateInput(d.settings.usd_inr_rate != null ? String(d.settings.usd_inr_rate) : ''); }).catch(() => {});
  };
  useEffect(refresh, []);

  const saveRate = async () => {
    setBusy('rate');
    setError('');
    try {
      await platformApi.patch('/ai/platform-settings', { usd_inr_rate: rateInput === '' ? null : Number(rateInput) });
      refresh();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };

  // Confirmed in a dialog first — window.confirm() is refused by embedded browsers.
  const markTaxReviewComplete = async () => {
    setBusy('tax-review');
    setError('');
    try {
      await platformApi.post('/ai/platform-settings/tax-review', {});
      refresh();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };

  const generate = async () => {
    if (!genTenantId) return;
    setBusy('generate');
    setError('');
    try {
      await platformApi.post('/ai/statements/generate', { tenant_id: genTenantId, period_month: genPeriod });
      refresh();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };

  const finalise = async (id: number) => {
    setBusy(`finalise-${id}`);
    setError('');
    try {
      await platformApi.post(`/ai/statements/${id}/finalise`, {});
      refresh();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };

  const markInvoiced = async (id: number) => {
    const draft = invoiceDraft[id];
    if (!draft?.number || !draft?.date) { setError('Enter an invoice number and date first.'); return; }
    setBusy(`invoice-${id}`);
    setError('');
    try {
      await platformApi.post(`/ai/statements/${id}/mark-invoiced`, { invoice_number: draft.number, invoice_date: draft.date });
      refresh();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>AI statements &amp; billing settings</div>
        <button onClick={onBack} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>← Back to tenants</button>
      </div>

      {error && <div style={{ padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Platform settings</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>USD → INR rate</label>
            <input type="number" step="0.01" value={rateInput} onChange={e => setRateInput(e.target.value)} placeholder="e.g. 83.5" style={{ width: 140 }} />
          </div>
          <button className="btn-primary" disabled={busy === 'rate'} onClick={saveRate}>Save</button>
        </div>
        <div style={{ fontSize: 12, color: '#6B7280' }}>
          Tax review (D29 go-live gate):{' '}
          {settings?.tax_review_completed_at
            ? <span style={{ color: '#166534', fontWeight: 600 }}>✓ Complete ({new Date(settings.tax_review_completed_at).toLocaleDateString()})</span>
            : <span style={{ color: '#991B1B', fontWeight: 600 }}>Not complete — statements cannot be finalised yet</span>}
        </div>
        {!settings?.tax_review_completed_at && (
          <button disabled={busy === 'tax-review'} onClick={() => setConfirmTaxReview(true)} style={{ marginTop: 10, background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
            Mark tax review complete
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Generate / refresh a statement</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>Tenant</label>
            <select value={genTenantId} onChange={e => setGenTenantId(e.target.value ? Number(e.target.value) : '')} style={{ width: '100%' }}>
              <option value="">Select a tenant…</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>Period (month)</label>
            <input type="date" value={genPeriod} onChange={e => setGenPeriod(e.target.value)} />
          </div>
          <button className="btn-primary" disabled={!genTenantId || busy === 'generate'} onClick={generate}>Generate draft</button>
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #E5E7EB', color: '#6B7280' }}>
              <th style={{ padding: '10px 14px' }}>Tenant</th>
              <th style={{ padding: '10px 14px' }}>Period</th>
              <th style={{ padding: '10px 14px' }}>Allowance</th>
              <th style={{ padding: '10px 14px' }}>Charge</th>
              <th style={{ padding: '10px 14px' }}>Overage</th>
              <th style={{ padding: '10px 14px' }}>Status</th>
              <th style={{ padding: '10px 14px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {statements.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>No statements generated yet.</td></tr>
            )}
            {statements.map(s => {
              const style = STATUS_STYLE[s.status];
              return (
                <tr key={s.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ padding: '10px 14px' }}>{s.tenant_name}</td>
                  <td style={{ padding: '10px 14px' }}>{s.period_month.slice(0, 7)}</td>
                  <td style={{ padding: '10px 14px' }}>{s.allowance_inr != null ? `₹${Number(s.allowance_inr).toLocaleString('en-IN')}` : '—'}</td>
                  <td style={{ padding: '10px 14px' }}>₹{Number(s.total_charge_inr ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td style={{ padding: '10px 14px' }}>₹{Number(s.overage_inr ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ background: style.bg, color: style.color, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{s.status}</span>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => downloadWithAuth(`/platform-api/ai/statements/${s.id}/csv`, `ai-statement-${s.tenant_slug}-${s.period_month}.csv`).catch(e => setError(e instanceof Error ? e.message : String(e)))}
                        style={{ fontSize: 12, background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                      >CSV</button>
                      <button
                        onClick={() => downloadWithAuth(`/platform-api/ai/statements/${s.id}/pdf`, `ai-statement-${s.tenant_slug}-${s.period_month}.pdf`).catch(e => setError(e instanceof Error ? e.message : String(e)))}
                        style={{ fontSize: 12, background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                      >PDF</button>
                      {s.status === 'draft' && (
                        <button disabled={busy === `finalise-${s.id}`} onClick={() => finalise(s.id)} style={{ fontSize: 12, background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>
                          Finalise
                        </button>
                      )}
                      {s.status === 'finalised' && (
                        <>
                          <input
                            placeholder="Invoice #"
                            value={invoiceDraft[s.id]?.number ?? ''}
                            onChange={e => setInvoiceDraft(prev => ({ ...prev, [s.id]: { number: e.target.value, date: prev[s.id]?.date ?? '' } }))}
                            style={{ width: 90, fontSize: 12 }}
                          />
                          <input
                            type="date"
                            value={invoiceDraft[s.id]?.date ?? ''}
                            onChange={e => setInvoiceDraft(prev => ({ ...prev, [s.id]: { number: prev[s.id]?.number ?? '', date: e.target.value } }))}
                            style={{ fontSize: 12 }}
                          />
                          <button disabled={busy === `invoice-${s.id}`} onClick={() => markInvoiced(s.id)} style={{ fontSize: 12, background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>
                            Mark invoiced
                          </button>
                        </>
                      )}
                      {s.status === 'invoiced' && (
                        <span style={{ fontSize: 12, color: '#6B7280' }}>{s.invoice_number} ({s.invoice_date})</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        open={confirmTaxReview}
        onOpenChange={setConfirmTaxReview}
        title="Mark the tax review as complete?"
        description="Statements can then be finalised. This cannot be undone from here."
        confirmLabel="Mark complete"
        variant="primary"
        onConfirm={() => { void markTaxReviewComplete(); }}
      />
    </div>
  );
}
